import { Link } from 'react-router-dom';
import '../App.css';
import Header from '../components/Header';
import { Helmet } from "react-helmet-async";

function Rehabilitation() {
  return (
    <>
    <Helmet>
        <title> Quality Rehabilitation Care at Whitstable House </title>
        <meta
          name="description"
          content="Our rehabilitation service is designed to support recovery after hospital, surgery, illness or functional decline. We offer a range of specialist equipment and services to help you get back on your feet."/>
        <link rel="canonical" href="https://www.whitstable.house/rehab"/>
      </Helmet>

    <div className="page">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header />

      <main id="main-content">
        <section className="content-section rehab-page">
          <div className="rehab-container">
            <h1>Physical Rehab Centre</h1>
          </div>
        </section>

        <section className="content-section rehab-intro">
          <div className="section__container">
            <div className="section__text">
              <h2>Service Overview</h2>
              <p>The Physical Rehab Centre is a CQC-registered, accommodation-based rehabilitation service providing structured, intensive recovery programmes for adults requiring more than sessional or home-based physiotherapy.</p>
              <p>The service is designed to support:</p>
              <ul>
                <li>Early discharge from acute hospital settings</li>
                <li>Post-operative orthopaedic recovery</li>
                <li>Neurological rehabilitation</li>
                <li>Cardiac and respiratory reconditioning</li>
                <li>Frailty and functional decline</li>
                <li>Sports injury rehabilitation</li>
                <li>Women's health rehabilitation</li>
              </ul>
              <p>The unit operates within <Link to="/care-suites" className="nurse-care-link">Whitstable House Care Suites</Link> and is supported by 24-hour, nurse-led clinical care.</p>
            </div>
            <div className="section__image">
              <img src="/reception-image.jpg" alt="Whitstable House Care Suites" />
            </div>
          </div>
        </section>

        <section className="content-section rehab-box rehab-governance">
          <div className="section__container">
            <div className="section__text">
              <h2>Clinical Governance & Professional Standards</h2>
              <p>The rehabilitation service is led by a Chartered Physiotherapist (MCSP), registered with the Health and Care Professions Council (HCPC), with 14 years' clinical experience across NHS and private practice.</p>
              <p>Chartered physiotherapists are active members of the Chartered Society of Physiotherapy and must maintain ongoing CPD, retain a professional portfolio, practise in accordance with evidence-based guidelines, and adhere to strict professional and ethical standards.</p>
              <p>This ensures high-quality, safe, up-to-date rehabilitation practice. The unit is fully CQC-registered as an accommodation-based service, with 24/7 in-house registered nursing support.</p>
            </div>
            <div className="section__image">
              <img src="/Physio_1(1).jpeg" alt="Clinical care at Whitstable House" />
            </div>
          </div>
        </section>

        <section className="content-section rehab-box rehab-equipment">
          <div className="section__container">
            <div className="section__text">
              <h2>Specialist Rehabilitation Equipment</h2>
              <p>The service includes access to advanced rehabilitation technology rarely available outside hospital settings:</p>
              <p><strong>AlterG Anti-Gravity Treadmill</strong> — Precise body-weight support, early weight-bearing post-surgery, gait retraining and symmetry analysis, fall-safe mobilisation.</p>
              <p><strong>HUR SmartBalance System</strong> — Objective balance assessment, fall-risk identification, cognitive-motor training, progress monitoring.</p>
              <p><strong>HUR Multifunction Rehabilitation Machines</strong> — Controlled, low-impact strength rebuilding, multi-muscle group training, range-limited safe joint rehabilitation, designed specifically for rehabilitation populations.</p>
              <p><strong>"The Shuffler"</strong> — Helps prevent falls, encourages low-impact rebuilding of muscle strength and tone, keeps individuals mobile for as long as possible, can be used whilst seated.</p>
            </div>
            <div className="section__image">
              <img src="/Anti_Gravity_2(1).jpeg" alt="Rehabilitation facilities" />
            </div>
          </div>
        </section>

        <section className="content-section rehab-box rehab-model">
          <div className="section__container">
            <div className="section__text">
              <h2>Rehabilitation Model</h2>
              <p><strong>Inpatient Rehabilitation</strong> — Patients stay in private accommodation while receiving structured daily physiotherapy, equipment-led progression, continuous clinical observation, medication oversight and multidisciplinary coordination. This model bridges the gap between acute hospital care and independent living.</p>
              <p><strong>Orthopaedic Early Recovery Pathway</strong> — For many hip and knee replacements, patients may transfer directly from hospital; mobilisation can begin immediately within a supported environment; hospital length of stay may be reduced. We work collaboratively with discharge teams and consultants.</p>
              <p><strong>Outpatient Continuation</strong> — Following inpatient rehabilitation, patients may continue structured therapy as outpatients, access specialist equipment, undergo reassessment and progression planning, or return for further rehabilitation blocks if required.</p>
            </div>
            <div className="section__image">
              <img src="/Anti_Gravity_3(1).jpeg" alt="Private accommodation for rehabilitation" />
            </div>
          </div>
        </section>

        <section className="content-section rehab-box rehab-pathway">
          <div className="section__container">
            <div className="section__text">
              <h2>Patient Pathway</h2>
              <p>On admission, patients receive: comprehensive subjective and objective assessment; movement, strength and balance evaluation; clear explanation of findings; collaborative goal-setting; and an individually prescribed programme. Treatment plans are realistic, lifestyle-focused and progression-based.</p>
              <p>Where appropriate, patients may be referred onward for consultant review, diagnostic imaging (X-ray, MRI), or additional specialist input.</p>
            </div>
            <div className="section__image">
              <img src="/moving-wellbeing.jpg" alt="Welcome and assessment" />
            </div>
          </div>
        </section>

        <section className="content-section rehab-box rehab-referral">
          <div className="section__container">
            <div className="section__text">
              <h2>Referral Pathways</h2>
              <p><strong>NHS Physiotherapy</strong> — Requires GP or consultant referral.</p>
              <p><strong>The Physical Rehab Centre</strong> — No GP referral required for private attendance. Patients may self-refer directly. If using private medical insurance, most insurers do not require GP referral; patients should confirm individual policy requirements.</p>
              <p><strong>We welcome referrals from:</strong> Consultants, GPs, hospital discharge teams, case managers and private insurers.</p>
            </div>
            <div className="section__image">
              <img src="/lounge-image.jpg" alt="Whitstable House Rehab Centre" />
            </div>
          </div>
        </section>

        <section className="content-section get-in-touch-nursing">
          <div className="section__container">
            <div className="section__text get-in-touch-content">
              <h2 className="get-in-touch-heading">Get in touch</h2>
              <p>For rehabilitation enquiries and referrals, speak to our Whitstable House team today.</p>
              <p>Call us: <a href="tel:01227533522" className="nurse-care-link">01227 533522</a> (Option 1)</p>
              <p>Email us: <a href="mailto:rehab@whitstable.house" className="nurse-care-link">rehab@whitstable.house</a></p>
              <p>Or complete our <Link to="/contact-us" className="nurse-care-link">enquiry form</Link> and we'll be in touch within 24 hours to discuss rehabilitation options.</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <section className="footer__enquiry">
          <div className="footer__enquiry-container">
            <Link to="/contact-us" className="footer__enquiry-link">
              Enquire about Whitstable House <span className="footer__arrow">▶</span>
            </Link>
          </div>
        </section>
        <div className="footer__container">
          <section className="contact-section">
            <div className="contact-section__content">
              <p>Whitstable House, Boorman Way, Whitstable CT5 3SE</p>
              <p>Copyright © Whitstable House</p>
            </div>
          </section>
        </div>
      </footer>
    </div>
    </>
  );
}

export default Rehabilitation;
